import {useCallback, useReducer} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import useApi from './useApi';


const reducer = (state, action) => {
  const {
    type,
    nextParams,
    nextFilters,
    data,
    clearBeforeFetch,
    func,
  } = action;

  switch (type) {
    case 'fetching': {
      return {
        ...state,
        rows: clearBeforeFetch ? [] : state.rows,
        rowsCount: clearBeforeFetch ? 0 : state.rowsCount,
        countPrivate: clearBeforeFetch ? 0 : state.countPrivate,
        isFetching: true,
        params: nextParams ? { ...nextParams } : {},
        filters: nextFilters ? { ...nextFilters } : {},
      };
    }

    case 'complete': {
      return {
        ...state,
        ...data,
        isFetching: false,
        init: true
      };
    }

    case 'setData': {
      return { ...state, ...data };
    }

    case 'setRows': {
      return {
        ...state,
        rows: [...data],
      };
    }

    case 'setRowByIndex': {
      const { row, index } = data;

      return {
        ...state,
        rows: state.rows.map((r, i) => (i === index ? row : r)),
      };
    }

    case 'updateRows': {
      return {
        ...state,
        rows: func(state.rows)
      };
    }

    default:
      throw new Error();
  }
};

export const useGrid = (endpoint, callback, clearBeforeFetch = true) => {
  const api = useApi();

  const [state, dispatch] = useReducer(reducer, {
    init: false,
    rowsCount: 0,
    countPrivate: 0,
    rows: [],
    perPage: 0,
    isFetching: false,
    params: {},
    filters: {},
  });

  const fetch = useCallback((params = null, filters = null) => {
    const nextParams = params || state.params;
    const nextFilters = filters || state.filters;

    const queryParams = nextFilters
      ? {
        ...(typeof nextFilters === 'object'
          ? nextFilters
          : { filters: nextFilters }),
        ...nextParams,
      }
      : nextParams;

    dispatch({ type: 'fetching', nextParams, nextFilters, clearBeforeFetch });

    return new Promise((resolve, reject) =>
      api.get(endpoint, queryParams, {
        handleError: response => reject({ response, code: 0 }),
        handleNotFound: response => reject({ response, code: 404 }),
        handleForbidden: response => reject({ response, code: 403 }),
        handleSuccess: response => {
          dispatch({ type: 'complete', data: response });
          resolve(nextParams, response);
        },
      })
    );
  }, [api, endpoint, state]);

  const setData = nextData => {
    dispatch({ type: 'setData', data: nextData });
  };

  const setDataRows = rows => {
    dispatch({ type: 'setRows', data: rows });
  };

  const setDataRowByIndex = (row, index) => {
    dispatch({ type: 'setRowByIndex', data: { row, index } });
  };

  const updateDataRows = func => {
    dispatch({ type: 'updateRows', func });
  };

  const readPage = page => {
    const nextParams = cloneDeep(state.params);
    nextParams.page = page;
    if (!nextParams.page) {
      delete nextParams.page;
    }
    return fetch(nextParams, state.filters).then(
      () => callback && callback(nextParams)
    );
  };

  const sortBy = sortBy => {
    const nextParams = {
      ...cloneDeep(state.params),
      ...(typeof sortBy === 'object' ? sortBy : { sortBy }),
    };

    if (Number(nextParams.page) === 0 || nextParams.page) {
      delete nextParams.page;
    }
    return fetch(nextParams, state.filters).then(
      () => callback && callback(nextParams)
    );
  };

  const filterBy = useCallback(filter => {
    const nextParams = {
      ...cloneDeep(state.params),
      ...(typeof filter === 'object' ? filter : { filter }),
    };

    if (Number(nextParams.page) === 0 || nextParams.page) {
      delete nextParams.page;
    }
    return fetch(nextParams, state.filters).then(
      () => callback && callback(nextParams)
    );
  }, [state]);

  const search = search => {
    const nextParams = { ...state.params, search };
    delete nextParams.page;
    if (!search) {
      delete nextParams.search;
    }
    return fetch(nextParams, state.filters).then(
      () => callback && callback(nextParams)
    );
  };

  return {
    data: state,
    actions: {
      fetch,
      setData,
      setDataRows,
      updateDataRows,
      setDataRowByIndex,
      readPage,
      sortBy,
      filterBy,
      search,
    },
  };
};
